import { Link, Text } from '@chakra-ui/react';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { NavLink as RouterLink } from 'react-router-dom';

import { Badge, BadgeStatus } from '@aslanpay/portal-common-lib';

import { ReconciliationCompanyStatusValues } from 'app/constants/reconciliations';

import { SalaryReconciliationCompany } from 'app/types/salaryReconciliations';

import { getCompanySalaryReconciliationProfilePath } from 'app/utils/routes';

/**
 * @description Columns of the companies a salary accrual reconciliation run covered. Each company links to its own
 * view of the run.
 */
function getSalaryReconciliationCompanyColumns(reconciliationId: string) {
  const columnHelper = createColumnHelper<SalaryReconciliationCompany>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<SalaryReconciliationCompany, any>[] = [
    columnHelper.accessor('companyName', {
      header: 'Company',
      enableSorting: false,
      cell: (info) => (
        <Link
          as={RouterLink}
          to={getCompanySalaryReconciliationProfilePath(
            info.row.original.companyId,
            reconciliationId
          )}
          color="brand.purple"
          data-testid={`salary-reconciliation-company-link-${info.row.original.companyId}`}
        >
          {info.getValue() || info.row.original.companyId}
        </Link>
      ),
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      enableSorting: false,
      cell: (info) => {
        const status = ReconciliationCompanyStatusValues[info.row.original.status];
        return (
          <Badge
            value={status?.value ?? info.getValue()}
            status={(status?.color ?? 'created') as BadgeStatus}
          />
        );
      },
    }),
    columnHelper.accessor('failureReason', {
      header: 'Why it failed',
      enableSorting: false,
      cell: (info) => (
        <Text maxW="360px" whiteSpace="normal">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('individualsChecked', {
      header: 'Individuals checked',
      enableSorting: false,
    }),
    columnHelper.accessor('individualsNotChecked', {
      header: 'Not checked',
      enableSorting: false,
    }),
    columnHelper.accessor('problems', {
      header: 'Problems',
      enableSorting: false,
    }),
    columnHelper.accessor('newProblems', {
      header: 'New',
      enableSorting: false,
    }),
    columnHelper.accessor('resolvedProblems', {
      header: 'Resolved',
      enableSorting: false,
    }),
  ];

  return columns;
}

export default getSalaryReconciliationCompanyColumns;
